/* eslint-disable jsx-a11y/img-redundant-alt */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { IconEdit, IconPlus, IconMinus } from "@tabler/icons-react";
import ListView from "../../../components/common/ListView";
import ToggleButton from "../../../components/ToggleButton";

import MessageModal from "../../../components/MessageModal";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import formatDate, { getFileExtension } from "../../../utilities/helper";
import NoDataFound from "../../../components/common/NoDataFound";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import Pagination from "../../../components/common/Pagination";
import NewinputField from "../../../components/NewInput";
import {
  useBoxCreateMutation,
  useFetchBoxData,
  useUpdateBoxes,
  useUpdateBoxesStatus,
} from "../../../apis/boxesApis";
import { useLanguage } from "../../../context/LanguageContext";

type FormValues = {
  image?: File | string;
  name?: string;
  weight?: string;
  width?: string;
  height?: string;
  length?: string;
  description?: string;
};

export default function BoxesTab() {
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [mode, setMode] = useState<"add" | "edit" | null>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [actionModal, setActionModal] = useState<boolean>(false);
  const { t } = useLanguage();

  const {
    data: boxData,
    isLoading,
    isFetching,
    refetch,
  } = useFetchBoxData(pageNo, limit, "");

  useEffect(() => {
    refetch();
  }, [pageNo]);

  const { mutate: createBoxes, isPending: createPending } =
    useBoxCreateMutation();
  const { mutate: updateBoxes, isPending: updatePending } = useUpdateBoxes();
  const { mutate: updateBoxesStatus, isPending: statusPending } =
    useUpdateBoxesStatus();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    watch,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: "",
      weight: "",
      width: "",
      height: "",
      length: "",
      description: "",
      image: undefined,
    },
  });

  const handleAddClick = () => {
    if (mode === "edit") {
      setMode("add");
      setSelectedItem(null);
    } else {
      setMode(mode === "add" ? null : "add");
      setSelectedItem(null);
      reset();
    }
  };

  const handleEditClick = (item: any) => {
    setSelectedItem(item);

    setValue("name", item.name);
    setValue("weight", item.weight);
    setValue("width", item.width);
    setValue("height", item.height);
    setValue("length", item.length);
    setValue("description", item.description);
    setValue("image", item.image);

    setMode("edit");
    setTimeout(() => {
      const selectors = [
        ".content",
        ".main-content",
        ".dashboard-content",
        "main",
        "[role='main']",
        ".overflow-y-auto",
        ".scrollable-content",
      ];

      let scrolled = false;
      for (const selector of selectors) {
        const el = document.querySelector(selector) as HTMLElement;
        if (el && el.scrollHeight > el.clientHeight) {
          el.scrollTo({ top: 0, behavior: "smooth" });
          scrolled = true;
          break;
        }
      }

      if (!scrolled) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 100);
  };

  const handleCancelEdit = () => {
    setMode(null);
    setSelectedItem(null);
    reset();
  };

  const handleUpdateBox = (item: any, updatedFields: any) => {
    updateBoxesStatus(
      {
        id: item.id,

        status: updatedFields.status ?? item.status,
        forApp: updatedFields.forApp ?? item.forApp,
        forSystem: updatedFields.forSystem ?? item.forSystem,
      },
      {
        onSuccess: () => {
          refetch();
          toast.success(t("Updated successfully"));
        },
        onError: (error: any) => {
          toast.error(error.message);
        },
      },
    );
  };

  const handleToggleStatus = (item: any) => {
    setSelectedItem(item);
    setActionModal(true);
  };

  const confirmUpdateStatus = () => {
    if (!selectedItem) return;

    handleUpdateBox(selectedItem, {
      status: selectedItem.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
    });

    setActionModal(false);
  };

  const onSubmit = (data: FormValues) => {
    const submitData = {
      name: data.name,
      weight: data.weight,
      width: data.width,
      height: data.height,
      length: data.length,
      description: data.description,
      image: data.image,
    };

    if (mode === "add") {
      createBoxes(submitData, {
        onSuccess: () => {
          reset();
          refetch();
          setMode(null);
          toast.success(t("Boxes added successfully"));
        },
        onError: (error) => toast.error(`${error}`),
      });
    } else if (mode === "edit") {
      updateBoxes(
        {
          id: selectedItem?.id,
          ...submitData,
        },
        {
          onSuccess: () => {
            reset();
            refetch();
            setMode(null);
            setSelectedItem(null);
            toast.success(t("Boxes updated successfully"));
          },
          onError: (error) => toast.error(`${error}`),
        },
      );
    }
  };

  const handlePaginationFetch = (page: number) => setPageNo(page);

  return (
    <>
      <div className="space-y-4 border border-[#d2d2d2] rounded-[20px] p-4 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] mb-10">
        <div className="flex justify-between items-center">
          <p className="text-lg font-medium text-[#212121]">{t("Boxes")}</p>
          <button
            onClick={handleAddClick}
            className="bg-[#FBD037] text-[#212121] text-sm font-semibold p-2 rounded-full"
          >
            {mode === "add" || mode === "edit" ? (
              <IconMinus stroke={1.5} size={20} />
            ) : (
              <IconPlus stroke={1.5} size={20} />
            )}
          </button>
        </div>

        {(mode === "add" || mode === "edit") && (
          <div className="bg-[#FCFDF8] p-4 rounded-lg border border-[#D2D2D2]">
            <h2 className="text-[#212121]/70 text-base font-medium mb-4">
              {mode === "add" ? t("Add Boxes") : t("Edit Boxes")}
            </h2>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="grid grid-cols-5 gap-4">
                <NewinputField
                  name="name"
                  title={t("Box Name")}
                  placeholder={t("Enter Box Name")}
                  className="w-full"
                  register={register}
                  error={errors.name}
                  validation={{
                    required: t("Box Name is required"),
                    pattern: {
                      value: /^(?=.*[A-Za-z])[A-Za-z0-9\s-]+$/,
                      message:
                        t("Box Name must include at least one letter (not only numbers)"),
                    },
                    validate: (value: any) => {
                      if (!value?.trim())
                        return t("Box Name cannot be empty or only spaces");
                      return true;
                    },
                  }}
                  type="text"
                />

                <NewinputField
                  name="weight"
                  type="text"
                  title={t("Min Weight (kg)")}
                  placeholder={t("Enter Min Weight (kg)")}
                  register={register}
                  error={errors.weight}
                  validation={{ required: t("Weight is required") }}
                />
                <NewinputField
                  name="width"
                  type="text"
                  title={t("Width")}
                  placeholder={t("Enter Width")}
                  register={register}
                  error={errors.width}
                  validation={{ required: t("Width is required") }}
                />
                <NewinputField
                  name="height"
                  type="text"
                  title={t("Height")}
                  placeholder={t("Enter Height")}
                  register={register}
                  error={errors.height}
                  validation={{ required: t("Height is required") }}
                />
                <NewinputField
                  name="length"
                  type="text"
                  title={t("Length")}
                  placeholder={t("Enter Length")}
                  register={register}
                  error={errors.length}
                  validation={{ required: t("Length is required") }}
                />
                <NewinputField
                  name="description"
                  type="text"
                  title={t("Description")}
                  placeholder={t("Enter Description")}
                  className="col-span-4"
                  register={register}
                  error={errors.description}
                  validation={{ required: t("Description is required") }}
                />
                <div className="flex items-center space-x-4 col-span-1">
                  <Controller
                    control={control}
                    name="image"
                    rules={{
                      validate: (file: File | string | undefined) => {
                        if (!file) return true;

                        if (typeof file === "string") return true;

                        const MAX_SIZE = 1 * 1024 * 1024;
                        if (file.size > MAX_SIZE) {
                          return t("File size must be 1 MB or less");
                        }

                        const allowedTypes = [
                          "image/jpeg",
                          "image/png",
                          "image/jpg",
                        ];
                        const fileObj = file instanceof File ? file : null;

                        if (fileObj) {
                          if (!allowedTypes.includes(fileObj.type)) {
                            const ext = getFileExtension(fileObj);
                            if (!["jpeg", "png", "jpg"].includes(ext)) {
                              return t("Only JPG, JPEG, or PNG allowed");
                            }
                          }
                        }
                        return true;
                      },
                    }}
                    render={({ field, fieldState }) => {
                      const fileURL =
                        field.value instanceof File
                          ? URL.createObjectURL(field.value)
                          : typeof field.value === "string" &&
                              field.value !== ""
                            ? field.value
                            : "/img/dummy-logo.png";

                      return (
                        <>
                          <img
                            src={fileURL}
                            className="size-16 rounded-xl object-cover border-2 border-dashed border-gray-300"
                            alt={t("Box Image")}
                          />

                          <div className="space-y-1">
                            <div className="flex items-center gap-3">
                              <input
                                type="file"
                                accept=".jpg,.jpeg,.png"
                                className="hidden"
                                id="image-upload"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    field.onChange(file);
                                    clearErrors("image");
                                  }
                                }}
                              />
                              <label
                                htmlFor="image-upload"
                                className="text-[#212121] cursor-pointer border rounded-lg text-sm px-4 py-1 border-[#FBD037] bg-[#FBD037]/20 hover:bg-[#FBD037]/30 transition"
                              >
                                {t("Upload")}
                              </label>

                              {field.value && (
                                <button
                                  type="button"
                                  className="text-red-500 text-sm hover:underline"
                                  onClick={() => {
                                    field.onChange(undefined);
                                  }}
                                >
                                  {t("Remove")}
                                </button>
                              )}
                            </div>

                            <p className="text-xs text-[#555]">
                              {t("Accepted formats: PNG, JPG (Max file size: 1 MB)")}
                            </p>

                            {fieldState.error && (
                              <p className="text-red-500 text-xs font-medium mt-1">
                                {fieldState.error.message}
                              </p>
                            )}
                          </div>
                        </>
                      );
                    }}
                  />
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  type="submit"
                  disabled={createPending || updatePending}
                  className={`bg-[#FBD037] text-[#212121] text-sm font-semibold px-4 py-2 rounded-full ${
                    createPending || updatePending
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {mode === "add" ? t("Save") : t("Update")}
                </button>

                {mode === "edit" && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="bg-[#000000] text-[#ffffff] text-sm font-semibold px-4 py-2 rounded-full"
                  >
                    {t("Cancel")}
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        <div className="-mx-4">
          {isLoading || isFetching ? (
            <TableSkeleton
              mainClass="bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5]"
              subClassName=""
              columns={[
                "#",
                t("Name"),
                t("Description"),
                t("Weight (kg)"),
                t("Width (cm)"),
                t("Length (cm)"),
                t("Created By"),
                t("Date"),
                t("Status"),
                t("Actions"),
              ]}
              rows={6}
            />
          ) : boxData.boxes?.length ? (
            <ListView
              mainDivClassName="bg-transparent"
              tdClassName="rounded-r-none rounded-l-none"
              trClassName="hover:bg-[#FBD037]/20"
              mailDivClassName="p-0 bg-transparent"
              thClassName="border-t border-r"
              data={boxData.boxes || []}
              columns={[
                {
                  label: "#",
                  render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
                },
                {
                  label: t("Name"),
                  render: (c: any) => (
                    <div className="flex items-center gap-2 w-24">
                      <img
                        src={c?.image || "/img/dummy-logo.png"}
                        className="w-10 h-10 rounded-md border object-cover"
                        alt={c.name}
                      />
                      <span className="truncate max-w-xs">{c.name}</span>
                    </div>
                  ),
                },
                { label: t("Description"), render: (c: any) => c.description },
                { label: t("Weight (kg)"), render: (c: any) => c.weight },
                { label: t("Width (cm)"), render: (c: any) => c.width },
                { label: t("Height (cm)"), render: (c: any) => c.height },
                { label: t("Length (cm)"), render: (c: any) => c.length },
                { label: t("Created By"), render: (c: any) => c.createdByName },
                { label: t("Date"), render: (c: any) => formatDate(c.createdAt) },
                {
                  label: t("Types"),
                  children: [
                    {
                      label: t("App"),
                      render: (c: any) => (
                        <label className="relative flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={c.forApp}
                            onChange={() =>
                              handleUpdateBox(c, {
                                forApp: !c.forApp,
                              })
                            }
                            className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                          />
                          <span
                            className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                                top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                          >
                            ✓
                          </span>
                        </label>
                      ),
                    },
                    {
                      label: t("System"),
                      render: (c: any) => (
                        <label className="relative flex justify-center items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={c.forSystem}
                            onChange={() =>
                              handleUpdateBox(c, {
                                forSystem: !c.forSystem,
                              })
                            }
                            className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                          />
                          <span
                            className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                                top-1/2 left-[22px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                          >
                            ✓
                          </span>
                        </label>
                      ),
                    },
                  ],
                },
                {
                  label: t("Status"),
                  render: (c: any) => (
                    <div className="flex justify-center items-center">
                      <ToggleButton
                        onChange={() => handleToggleStatus(c)}
                        checked={c.status === "ACTIVE"}
                      />
                    </div>
                  ),
                },
                {
                  label: t("Actions"),
                  render: (c: any) => (
                    <div className="flex justify-center items-center">
                      <button
                        onClick={() => handleEditClick(c)}
                        className="p-2 border border-[#D2D2D2] rounded-full"
                      >
                        <IconEdit strokeWidth={1.5} size={20} />
                      </button>
                    </div>
                  ),
                },
              ]}
            />
          ) : (
            <NoDataFound
              subClassName=""
              mainClassName="bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] rounded-b-3xl"
            />
          )}

          {boxData?.totalPages > 1 && (
            <div className="flex justify-center items-center pt-4">
              <Pagination
                currentPage={pageNo}
                totalPages={boxData?.totalPages}
                fetchData={handlePaginationFetch}
              />
            </div>
          )}
        </div>
      </div>

      <MessageModal
        open={actionModal}
        onClose={() => setActionModal(false)}
        title={t("Confirmation Required")}
        subtitle={t("Are you sure you want to perform this action?")}
        img={
          selectedItem?.status === "ACTIVE"
            ? "/img/inactive.svg"
            : "/img/active.svg"
        }
        showFooter={true}
        closeButtonText={t("Cancel")}
        actionButtonText={t("Confirm")}
        actionButtonOnClick={confirmUpdateStatus}
        isPending={statusPending}
        size="md"
      />
    </>
  );
}
